import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, X } from "lucide-react";

import { hasTsSession, isRiderIdentity, loadTsIdentity } from "@/lib/tsIdentity";
import { isTeamHubHost } from "@/lib/teamHub";
import { OPS_MOUNTED } from "@/lib/opsBase";
import { isEmbeddedStaffView, STAFF_APP_HOME, staffAppKeyForPath, staffAppsFor } from "@/lib/staffApps";

const PINK = "#EE2677";

/**
 * The global staff navigation rail.
 *
 * One thin strip above every staff app: a fixed way back to Team, and the apps
 * this employee can actually open, with the current one marked. Desktop gets
 * compact pills; on a phone the pills would not fit, so it collapses to the
 * current app plus a sheet.
 *
 * Not rendered on the Team page itself. Every destination it offers is already
 * a card there, so it would be a smaller, description-less duplicate of that
 * page — and nobody navigates home in order to use a nav bar. Team is where
 * this sends you, not one of its stops.
 *
 * It renders nothing at all unless this is the staff hub host, a TS session
 * exists, and the document is not embedded. Those three checks are why it can
 * never appear on a customer page: customer routes are served from a different
 * route tree on a different host, and the embedded views (TC, Chat Report) are
 * framed by a parent that is already drawing this rail.
 *
 * "Back" is a real link to the Team hub, never `history.back()`: staff arrive
 * inside these apps from QR codes, PWA shortcuts and notifications, where there
 * is no history entry to go back to.
 */
export default function StaffNavRail({ current, inset = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const pathname = location.pathname;
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Staff hub only, signed in only, never inside a frame or an ?embed=1 view.
  if (!isTeamHubHost() || isEmbeddedStaffView(location.search) || !hasTsSession()) return null;

  const identity = loadTsIdentity();
  // Delivery partners are not staff; they get the Delivery app and nothing else.
  if (!identity || isRiderIdentity(identity)) return null;

  /* Ops mounts the router with `/ops` as its basename, so `pathname` there is
     already stripped of the prefix — the dashboard reads as "/", which would
     resolve to the Team home key. Ops therefore declares its own identity, and
     it is the only caller that needs to: everywhere else the path is the truth. */
  const currentKey = current || staffAppKeyForPath(pathname);
  const apps = staffAppsFor(identity, { currentKey });
  if (!apps.length) return null;

  const currentApp = apps.find((app) => app.key === currentKey) || null;

  const pillStyle = (app) => ({
    "--rail-accent": app.accent,
    "--rail-accent-soft": `${app.accent}14`,
    "--rail-accent-ring": `${app.accent}59`,
  });

  const appLink = (app, className, onClick) => {
    const isCurrent = app.key === currentKey;
    const shared = {
      className,
      style: pillStyle(app),
      "aria-current": isCurrent ? "page" : undefined,
      "data-testid": `staff-rail-app-${app.key}`,
      onClick,
    };
    const Icon = app.icon;
    const body = (
      <>
        <Icon size={16} aria-hidden />
        <span>{app.label}</span>
      </>
    );
    /* A document load is required either when the target sits under a different
       basename (Ops), or when we are *inside* Ops and everything else sits
       outside it — `<Link to="/kitchen">` under the /ops basename resolves to
       /ops/kitchen, which is not a route. */
    return app.hardNav || OPS_MOUNTED ? (
      <a key={app.key} href={app.path} {...shared}>
        {body}
      </a>
    ) : (
      <Link key={app.key} to={app.path} {...shared}>
        {body}
      </Link>
    );
  };

  return (
    <nav
      className={`staff-rail${inset ? " staff-rail--inset" : ""}`}
      aria-label="Staff apps"
      data-testid="staff-nav-rail"
    >
      <style>{`
        .staff-rail {
          background: #fff; border-bottom: 1px solid rgba(45,45,45,0.09);
          padding-top: env(safe-area-inset-top, 0px);
          position: relative; z-index: 45;
          font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
        }
        .staff-rail * { box-sizing: border-box; }
        /* Mounted inside a page that already owns the top of the viewport (Ops
           has its own fixed header), so the safe-area inset belongs to that
           header, not to this. */
        .staff-rail--inset {
          padding-top: 0; border-bottom: none;
          border: 1px solid rgba(45,45,45,0.09); border-radius: 12px; margin-bottom: 16px;
        }
        /* Thin strip, but every target inside it still clears 40px. */
        .staff-rail-inner {
          display: flex; align-items: center; gap: 8px; min-height: 44px;
          padding: 2px max(12px, env(safe-area-inset-right, 0px)) 2px max(12px, env(safe-area-inset-left, 0px));
        }
        .staff-rail-home {
          display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0;
          min-height: 40px; padding: 0 10px 0 6px; border-radius: 10px;
          color: ${PINK}; font-size: 14px; font-weight: 800;
          text-decoration: none; background: transparent; border: none; cursor: pointer;
        }
        .staff-rail-divider {
          width: 1px; height: 20px; background: rgba(45,45,45,0.12); flex-shrink: 0;
        }
        .staff-rail-apps { display: none; }
        .staff-rail-pill {
          display: inline-flex; align-items: center; gap: 6px;
          min-height: 40px; padding: 0 12px; border-radius: 999px;
          font-size: 13px; font-weight: 800; text-decoration: none;
          color: rgba(45,45,45,0.6); border: 1px solid transparent;
          white-space: nowrap; transition: background 120ms ease, color 120ms ease;
        }
        .staff-rail-pill[aria-current="page"] {
          color: var(--rail-accent); background: var(--rail-accent-soft);
          border-color: var(--rail-accent-ring);
        }
        @media (hover: hover) {
          .staff-rail-pill:hover:not([aria-current="page"]) {
            color: #2D2D2D; background: rgba(45,45,45,0.05);
          }
        }
        .staff-rail-pill:focus-visible, .staff-rail-home:focus-visible,
        .staff-rail-switch:focus-visible, .staff-rail-sheet-item:focus-visible {
          outline: 2px solid ${PINK}; outline-offset: 2px;
        }

        /* Phone: the pills will not fit, so the rail shows where you are and
           opens a sheet to move. */
        .staff-rail-switch {
          display: inline-flex; align-items: center; gap: 6px; margin-left: auto;
          min-height: 40px; padding: 0 10px; border-radius: 999px;
          background: rgba(45,45,45,0.05); border: 1px solid transparent;
          font-size: 13px; font-weight: 800; color: #2D2D2D; cursor: pointer;
          max-width: 60%; white-space: nowrap;
        }
        .staff-rail-switch-label { overflow: hidden; text-overflow: ellipsis; }
        .staff-rail-sheet {
          position: fixed; inset: 0; z-index: 60; background: rgba(0,0,0,0.3);
          display: flex; align-items: flex-end;
        }
        .staff-rail-sheet-panel {
          background: #fff; width: 100%; border-radius: 18px 18px 0 0;
          padding: 6px 10px calc(10px + env(safe-area-inset-bottom, 0px));
          max-height: 76dvh; overflow-y: auto;
        }
        .staff-rail-sheet-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 6px 4px;
        }
        .staff-rail-sheet-title {
          font-size: 12px; font-weight: 800; letter-spacing: 0.08em;
          text-transform: uppercase; color: rgba(45,45,45,0.45);
        }
        .staff-rail-sheet-close {
          display: inline-flex; align-items: center; justify-content: center;
          width: 40px; height: 40px; border-radius: 10px;
          border: none; background: transparent; color: rgba(45,45,45,0.6); cursor: pointer;
        }
        .staff-rail-sheet-item {
          display: flex; align-items: center; gap: 12px; width: 100%;
          min-height: 48px; padding: 0 10px; border-radius: 12px;
          font-size: 15px; font-weight: 800; color: #2D2D2D; text-decoration: none;
        }
        .staff-rail-sheet-item[aria-current="page"] {
          color: var(--rail-accent); background: var(--rail-accent-soft);
        }

        @media (min-width: 768px) {
          .staff-rail-apps {
            display: flex; align-items: center; gap: 4px;
            flex-wrap: wrap; min-width: 0;
          }
          .staff-rail-switch { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .staff-rail-pill { transition: none; }
        }
      `}</style>

      <div className="staff-rail-inner">
        {/* Never history.back(): staff reach these apps from QR codes and PWA
            shortcuts, where the previous entry is not Team — or does not exist. */}
        {OPS_MOUNTED ? (
          <a className="staff-rail-home" href={STAFF_APP_HOME.path} data-testid="staff-rail-home">
            <ChevronLeft size={18} aria-hidden />
            {STAFF_APP_HOME.label}
          </a>
        ) : (
          <Link className="staff-rail-home" to={STAFF_APP_HOME.path} data-testid="staff-rail-home">
            <ChevronLeft size={18} aria-hidden />
            {STAFF_APP_HOME.label}
          </Link>
        )}
        <span className="staff-rail-divider" aria-hidden />

        <div className="staff-rail-apps">{apps.map((app) => appLink(app, "staff-rail-pill"))}</div>

        {currentApp ? (
          <button
            type="button"
            className="staff-rail-switch"
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-haspopup="menu"
            data-testid="staff-rail-switch"
          >
            <currentApp.icon size={16} aria-hidden style={{ color: currentApp.accent }} />
            <span className="staff-rail-switch-label">{currentApp.label}</span>
            <ChevronDown size={15} aria-hidden />
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          className="staff-rail-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Switch app"
          onClick={() => setOpen(false)}
          data-testid="staff-rail-sheet"
        >
          <div className="staff-rail-sheet-panel" onClick={(event) => event.stopPropagation()}>
            <div className="staff-rail-sheet-head">
              <span className="staff-rail-sheet-title">Switch app</span>
              <button
                type="button"
                className="staff-rail-sheet-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={18} aria-hidden />
              </button>
            </div>
            {apps.map((app) => appLink(app, "staff-rail-sheet-item", () => setOpen(false)))}
          </div>
        </div>
      ) : null}
    </nav>
  );
}
